import React from 'react';
import { History, Smartphone, Users, ArrowDown } from 'lucide-react';
import { content } from '../data/content';

const Trends: React.FC = () => {
  const { trends } = content;

  // Custom animation for the title to "highlight the problem"
  // Using a gradient text effect that shimmers
  const titleAnimation = "animate-gradient bg-gradient-to-r from-gray-900 via-primary-600 to-gray-900 bg-[length:200%_auto] text-transparent bg-clip-text";

  return (
    <section className="min-h-screen flex items-center justify-center py-24 bg-white relative overflow-hidden">
      {/* Background decoration */}
      <div className="absolute top-0 left-0 w-full h-full bg-[radial-gradient(ellipse_at_top_left,_var(--tw-gradient-stops))] from-gray-50 via-white to-transparent -z-10"></div>

      <div className="container mx-auto px-6">
        <div className="text-center mb-20 max-w-3xl mx-auto">
          <h2 className={`text-3xl md:text-5xl font-bold mb-6 tracking-tight ${titleAnimation} drop-shadow-sm`}>
            {trends.title}
          </h2>
          <p className="text-gray-500 text-lg md:text-xl leading-relaxed animate-fade-in-up delay-100">
            保险业务正在从纯线下转向<span className="text-primary-600 font-bold mx-1">「线下 + 视频」</span>的新模式
          </p>
        </div>
        
        <div className="grid md:grid-cols-3 gap-8">
          {/* Card 1: 获客方式剧变 */}
          <div className="bg-white p-10 rounded-3xl shadow-[0_8px_30px_rgba(0,0,0,0.04)] border border-gray-100 hover:shadow-[0_20px_40px_rgba(0,0,0,0.08)] transition-all duration-500 group animate-fade-in-up delay-200 flex flex-col items-center text-center">
            <div className="w-20 h-20 bg-blue-50 rounded-2xl flex items-center justify-center text-blue-600 mb-8 group-hover:scale-110 transition-transform duration-300">
              <History className="w-10 h-10" />
            </div>
            <h3 className="text-2xl font-bold mb-8 text-gray-900">{trends.cards[0].title}</h3>
            
            <div className="w-full space-y-4">
              <div className="bg-white border border-gray-100 p-4 rounded-xl shadow-sm">
                <span className="text-gray-400 text-sm block mb-1">过去</span>
                <span className="text-gray-600 font-medium">亲友介绍 + 线下讲座</span>
              </div>
              <div className="flex justify-center py-2">
                <ArrowDown className="w-5 h-5 text-gray-300 animate-bounce" />
              </div>
              <div className="bg-blue-50 border border-blue-100 p-4 rounded-xl shadow-sm">
                <span className="text-blue-500 text-sm block mb-1">现在</span>
                <span className="text-blue-700 font-bold">短视频 + 社交媒体 + 内容信任</span>
              </div>
            </div>
          </div>

          {/* Card 2: 客户习惯改变 */}
          <div className="bg-white p-10 rounded-3xl shadow-[0_8px_30px_rgba(0,0,0,0.04)] border border-gray-100 hover:shadow-[0_20px_40px_rgba(0,0,0,0.08)] transition-all duration-500 group animate-fade-in-up delay-500 flex flex-col items-center text-center">
            <div className="w-20 h-20 bg-indigo-50 rounded-2xl flex items-center justify-center text-indigo-600 mb-8 group-hover:scale-110 transition-transform duration-300">
              <Smartphone className="w-10 h-10" />
            </div>
            <h3 className="text-2xl font-bold mb-6 text-gray-900">{trends.cards[1].title}</h3>
            
            <div className="text-gray-500 mb-8 leading-relaxed text-left space-y-3 w-full pl-4 border-l-2 border-indigo-100">
              <p>先在网上看你讲什么</p>
              <p>确认理念是否认同</p>
              <p>再决定是否愿意建立信任</p>
            </div>
            
            <div className="mt-auto w-full bg-indigo-50 py-3 px-4 rounded-full text-indigo-600 font-bold text-sm md:text-base">
              {trends.cards[1].highlight}
            </div>
          </div>

          {/* Card 3: 竞争态势 */}
          <div className="relative bg-white p-10 rounded-3xl shadow-[0_8px_30px_rgba(147,51,234,0.1)] border-2 border-purple-400 hover:shadow-[0_20px_40px_rgba(147,51,234,0.2)] transition-all duration-500 group animate-fade-in-up delay-700 flex flex-col items-center text-center overflow-hidden ring-4 ring-purple-50">
            {/* Background Highlight */}
            <div className="absolute inset-0 bg-gradient-to-b from-purple-50/50 to-transparent -z-10"></div>
            
            <div className="w-20 h-20 bg-purple-100 rounded-2xl flex items-center justify-center text-purple-600 mb-8 group-hover:scale-110 transition-transform duration-300 shadow-inner">
              <Users className="w-10 h-10" />
            </div>
            <h3 className="text-2xl font-bold mb-8 text-purple-900">{trends.cards[2].title}</h3>
            
            <ul className="w-full space-y-6 text-left">
              <li className="flex items-start gap-3 bg-white/80 p-3 rounded-xl border border-purple-100 shadow-sm">
                <span className="mt-2 w-2 h-2 rounded-full bg-purple-500 flex-shrink-0 shadow-[0_0_8px_rgba(168,85,247,0.6)]"></span>
                <span className="text-gray-700 leading-relaxed">
                  <span className="font-bold text-purple-700">有内容</span>
                  ：更容易招募新人，更容易获得转介绍。
                </span>
              </li>
              <li className="flex items-start gap-3 bg-white/80 p-3 rounded-xl border border-gray-100">
                <span className="mt-2 w-2 h-2 rounded-full bg-gray-300 flex-shrink-0"></span>
                <span className="text-gray-500 leading-relaxed">
                  <span className="font-bold text-gray-700">没内容</span>
                  ：新一代客户根本看不到你，逐渐边缘化。
                </span>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Trends;